import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLock } from '../context/LockContext';
import './LockScreen.css';

function LockScreen() {
  const { t } = useTranslation();
  const { hasPin, setPin, unlock } = useLock();
  const [pin, setLocalPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);

  const isSetupMode = !hasPin;

  const handleNumberClick = useCallback(
    (num) => {
      if (error) setError('');
      if (shake) setShake(false);

      if (isSetupMode) {
        if (isConfirming) {
          if (confirmPin.length < 6) {
            setConfirmPin(confirmPin + num);
          }
        } else {
          if (pin.length < 6) {
            setLocalPin(pin + num);
          }
        }
      } else {
        // Modo desbloqueo
        if (pin.length < 6) {
          setLocalPin(pin + num);
        }
      }
    },
    [error, shake, isSetupMode, isConfirming, confirmPin, pin]
  );

  const handleSubmit = useCallback(() => {
    if (isSetupMode) {
      if (isConfirming) {
        if (confirmPin === pin) {
          try {
            setPin(confirmPin);
            setError('');
          } catch (err) {
            setError(t('lock.setupError'));
            setTimeout(() => {
              setLocalPin('');
              setConfirmPin('');
              setIsConfirming(false);
            }, 1500);
          }
        } else {
          setError(t('lock.pinMismatch'));
          setShake(true);
          setTimeout(() => {
            setLocalPin('');
            setConfirmPin('');
            setIsConfirming(false);
            setShake(false);
          }, 1500);
        }
      } else {
        setIsConfirming(true);
      }
    } else {
      const success = unlock(pin);
      if (!success) {
        setError(t('lock.incorrectPin'));
        setShake(true);
        setTimeout(() => {
          setLocalPin('');
          setShake(false);
        }, 1000);
      }
    }
  }, [isSetupMode, isConfirming, confirmPin, pin, setPin, unlock, t]);

  const handleBackspace = useCallback(() => {
    if (error) setError('');
    if (shake) setShake(false);

    if (isSetupMode) {
      if (isConfirming) {
        setConfirmPin((prev) => prev.slice(0, -1));
      } else {
        setLocalPin((prev) => prev.slice(0, -1));
      }
    } else {
      setLocalPin((prev) => prev.slice(0, -1));
    }
  }, [error, shake, isSetupMode, isConfirming]);

  // Soporte para teclado físico (numpad y teclado normal)
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Números del teclado numérico (Numpad0-Numpad9) o teclado normal (Digit0-Digit9)
      // event.key devuelve '0'-'9' para ambos casos
      if (/^[0-9]$/.test(event.key)) {
        event.preventDefault();
        handleNumberClick(event.key);
      }
      // Backspace para borrar
      else if (event.key === 'Backspace') {
        event.preventDefault();
        handleBackspace();
      }
      // Enter to submit
      else if (event.key === 'Enter') {
        event.preventDefault();
        const currentLen = isSetupMode && isConfirming ? confirmPin.length : pin.length;
        if (currentLen >= 4) {
          handleSubmit();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    handleBackspace,
    handleNumberClick,
    handleSubmit,
    isSetupMode,
    isConfirming,
    confirmPin,
    pin,
  ]);

  const currentPin = isSetupMode && isConfirming ? confirmPin : pin;

  return (
    <div className="lock-screen-overlay">
      <div className="lock-screen-container">
        <div className="lock-icon">🔒</div>

        <h2 className="lock-title">
          {isSetupMode
            ? isConfirming
              ? t('lock.confirmPin')
              : t('lock.setupPin')
            : t('lock.enterPin')}
        </h2>

        {error && <div className="lock-error">{error}</div>}

        <div className={`pin-dots ${shake ? 'shake' : ''}`}>
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <div
              key={index}
              className={`pin-dot ${currentPin.length > index ? 'filled' : ''} ${index >= 4 ? 'optional' : ''}`}
            />
          ))}
        </div>

        <div className="pin-keypad">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button
              type="button"
              key={num}
              className="pin-button"
              onClick={() => handleNumberClick(num.toString())}
            >
              {num}
            </button>
          ))}
          <button
            type="button"
            className="pin-button confirm-button"
            onClick={handleSubmit}
            disabled={currentPin.length < 4}
          >
            ✓
          </button>
          <button type="button" className="pin-button" onClick={() => handleNumberClick('0')}>
            0
          </button>
          <button type="button" className="pin-button backspace" onClick={handleBackspace}>
            ⌫
          </button>
        </div>

        {isSetupMode && <p className="lock-hint">{t('lock.setupHint')}</p>}
      </div>
    </div>
  );
}

export default LockScreen;
